/**
 * LilliButton.jsx — floating launcher for the internal Lilli assistant.
 *
 * Bottom-right corner, copper pulse on unanswered responses. Only renders
 * when the user is authenticated AND their email is on the allow-list
 * (Oliver, Rocio via book@villaexperience.com; Albriet via website@…).
 *
 * Launcher z-index 45 — above z-40 page chrome (Villas' add-FAB, the
 * template-detail bottom bars) but below the z-50 overlay layer (ui/Modal,
 * Sidebar's mobile drawer) and the z-[60] hand-rolled modals, so it never
 * blocks a dialog. The opened chat panel (LilliChat) is z-[56] and still
 * floats above z-50 overlays — pre-existing behavior, untouched here.
 * Hidden on /inbox, where it overlapped the message-list action bar.
 */
import React, { useState, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Sparkles, X } from 'lucide-react'
import LilliChat from './LilliChat'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'

const COPPER = '#cfa190'
const DARK   = '#1e2a2e'

// Only these accounts get the launcher in phase 1.
const ALLOWED_EMAILS = new Set([
  'book@villaexperience.com',
  'website@villaexperience.com'
])

export default function LilliButton() {
  const [open, setOpen]     = useState(false)
  const [unread, setUnread] = useState(false)
  const [capabilityAllowed, setCapabilityAllowed] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  const { user, accessToken } = useAuth()
  const { pathname } = useLocation()
  const email = (user?.email || '').toLowerCase()

  useEffect(() => {
    if (!accessToken) {
      setCapabilityAllowed(false)
      return
    }
    let cancelled = false
    api.get('/lilli/capability')
      .then(({ data }) => {
        if (!cancelled) setCapabilityAllowed(!!data?.allowed)
      })
      .catch(() => {
        if (!cancelled) setCapabilityAllowed(ALLOWED_EMAILS.has(email))
      })
    return () => { cancelled = true }
  }, [accessToken, email])

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!media) return
    const update = () => setPrefersReducedMotion(media.matches)
    update()
    media.addEventListener?.('change', update)
    return () => media.removeEventListener?.('change', update)
  }, [])

  const isAllowed = !!accessToken && capabilityAllowed

  const handleUnreadChange = useCallback((v) => setUnread(!!v), [])

  if (!isAllowed || pathname.startsWith('/inbox')) return null

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close Lilli' : 'Open Lilli'}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[45] w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        style={{
          background: DARK,
          color: '#fff',
          boxShadow: '0 8px 24px rgba(30,42,46,0.35)'
        }}
      >
        {/* Subtle copper pulse when there is an unanswered response */}
        {unread && !open && (
          <span
            className={`absolute inset-0 rounded-full${prefersReducedMotion ? '' : ' animate-ping'}`}
            style={{ background: COPPER, opacity: 0.35 }}
            aria-hidden="true"
          />
        )}
        <span className="relative z-[1] flex items-center justify-center">
          {open ? (
            <X size={22} />
          ) : (
            <Sparkles size={22} style={{ color: COPPER }} />
          )}
        </span>
      </button>

      <LilliChat
        open={open}
        onClose={() => setOpen(false)}
        onUnreadChange={handleUnreadChange}
      />
    </>
  )
}
